import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Smartphone, Loader2, Wifi, Clock, QrCode, RefreshCw, ChevronLeft, Wallet, CheckCircle2,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

function fmtBytes(b) {
  if (!b) return "0 MB";
  const gb = b / 1024 ** 3;
  return gb >= 1 ? `${gb.toFixed(2)} GB` : `${(b / 1024 ** 2).toFixed(0)} MB`;
}

function EsimCard({ esim, onShowQr }) {
  const usagePct = esim.volume_gb ? Math.min(100, (esim.usage_bytes / (esim.volume_gb * 1024 ** 3)) * 100) : 0;
  return (
    <div className="bg-white border border-black/10 rounded-2xl p-5" data-testid={`esim-order-${esim.id}`}>
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="font-heading font-bold">{esim.name}</div>
          <div className="text-xs text-zinc-500 mt-0.5">
            Bought {new Date(esim.created_at).toLocaleDateString()} · ${esim.retail_usd?.toFixed(2)}
          </div>
        </div>
        <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold uppercase ${
          esim.status === "active" ? "bg-emerald-100 text-emerald-700"
          : esim.status === "ready" ? "bg-blue-100 text-blue-700"
          : "bg-amber-100 text-amber-700"}`}>
          {esim.status}
        </span>
      </div>
      {esim.status !== "provisioning" && (
        <>
          <div className="mt-3">
            <div className="flex justify-between text-xs text-zinc-500 mb-1">
              <span>Data used: {fmtBytes(esim.usage_bytes)}</span>
              <span>{esim.volume_gb} GB total</span>
            </div>
            <div className="h-2 bg-zinc-100 rounded-full overflow-hidden">
              <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${usagePct}%` }} />
            </div>
          </div>
          <button
            onClick={() => onShowQr(esim)}
            data-testid={`esim-show-qr-${esim.id}`}
            className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline"
          >
            <QrCode className="h-4 w-4" /> View QR & install
          </button>
        </>
      )}
      {esim.status === "provisioning" && (
        <div className="mt-3 flex items-center gap-2 text-sm text-amber-700">
          <Loader2 className="h-4 w-4 animate-spin" /> Being issued — refresh in a few seconds
        </div>
      )}
    </div>
  );
}

export default function Esim() {
  const { user, setBalance } = useAuth();
  const [locations, setLocations] = useState([]);
  const [location, setLocation] = useState("UG");
  const [packages, setPackages] = useState([]);
  const [loadingPkgs, setLoadingPkgs] = useState(false);
  const [buying, setBuying] = useState(null);
  const [myEsims, setMyEsims] = useState([]);
  const [loadingMy, setLoadingMy] = useState(false);
  const [qrEsim, setQrEsim] = useState(null);
  const [confirmPkg, setConfirmPkg] = useState(null);
  const [tab, setTab] = useState("shop");

  useEffect(() => {
    api.get("/esim/locations").then(({ data }) => setLocations(data.locations)).catch(() => {});
    loadMy();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    setLoadingPkgs(true);
    api.get(`/esim/packages?location=${location}`)
      .then(({ data }) => setPackages(data.packages))
      .catch((err) => toast.error(apiError(err.response?.data?.detail)))
      .finally(() => setLoadingPkgs(false));
  }, [location]);

  const loadMy = () => {
    setLoadingMy(true);
    api.get("/esim/my").then(({ data }) => setMyEsims(data.esims)).catch(() => {}).finally(() => setLoadingMy(false));
  };

  const buy = async (pkg) => {
    setBuying(pkg.package_code);
    try {
      const { data } = await api.post("/esim/purchase", { package_code: pkg.package_code });
      setBalance(data.balance);
      setConfirmPkg(null);
      toast.success("eSIM purchased!");
      setTab("my");
      loadMy();
    } catch (err) {
      setConfirmPkg(null);
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBuying(null);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight flex items-center gap-2">
            <Smartphone className="h-6 w-6 text-primary" /> Travel eSIMs
          </h1>
          <p className="text-sm text-zinc-500 mt-1">Instant data plans for your trip — delivered as a QR code.</p>
        </div>
        <div className="text-right">
          <div className="text-xs text-zinc-400 uppercase font-bold">Balance</div>
          <div className="font-mono font-bold text-lg" data-testid="esim-balance">${(user?.balance ?? 0).toFixed(2)}</div>
        </div>
      </div>

      <div className="flex gap-2 mb-6">
        <button onClick={() => setTab("shop")} data-testid="esim-tab-shop"
          className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${tab === "shop" ? "bg-primary text-white" : "bg-white border border-black/10 text-zinc-600"}`}>
          Shop plans
        </button>
        <button onClick={() => { setTab("my"); loadMy(); }} data-testid="esim-tab-my"
          className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${tab === "my" ? "bg-primary text-white" : "bg-white border border-black/10 text-zinc-600"}`}>
          My eSIMs {myEsims.length > 0 && `(${myEsims.length})`}
        </button>
      </div>

      {tab === "shop" && (
        <>
          <div className="flex gap-2 overflow-x-auto pb-2 mb-5 -mx-1 px-1">
            {locations.map((l) => (
              <button key={l.code} onClick={() => setLocation(l.code)} data-testid={`esim-location-${l.code}`}
                className={`shrink-0 px-3.5 py-2 rounded-xl text-sm font-semibold border transition-colors ${
                  location === l.code ? "bg-primary text-white border-primary" : "bg-white border-black/10 text-zinc-600 hover:border-primary/40"}`}>
                {l.flag} {l.name}
              </button>
            ))}
          </div>

          {loadingPkgs ? (
            <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-4" data-testid="esim-package-grid">
              {packages.map((p) => (
                <div key={p.package_code} className="bg-white border border-black/10 rounded-2xl p-5 hover:border-primary/40 hover:shadow-md transition-all">
                  <div className="font-heading font-bold text-sm">{p.name}</div>
                  <div className="flex items-center gap-4 mt-2 text-xs text-zinc-500">
                    <span className="inline-flex items-center gap-1"><Wifi className="h-3.5 w-3.5" /> {p.volume_gb} GB</span>
                    <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {p.duration_days} days</span>
                  </div>
                  <div className="flex items-end justify-between mt-4">
                    <div className="font-mono font-bold text-2xl">${p.price.toFixed(2)}</div>
                    <button onClick={() => setConfirmPkg(p)} data-testid={`esim-buy-${p.package_code}`}
                      className="px-4 py-2 rounded-xl bg-primary text-white text-sm font-semibold hover:opacity-90 transition-opacity">
                      Buy
                    </button>
                  </div>
                </div>
              ))}
              {packages.length === 0 && <p className="text-zinc-500 text-sm col-span-2 py-8 text-center">No plans available for this destination yet.</p>}
            </div>
          )}
        </>
      )}

      {tab === "my" && (
        <div className="space-y-4">
          <button onClick={loadMy} className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-primary" data-testid="esim-refresh-my">
            <RefreshCw className={`h-3.5 w-3.5 ${loadingMy ? "animate-spin" : ""}`} /> Refresh
          </button>
          {myEsims.map((e) => <EsimCard key={e.id} esim={e} onShowQr={setQrEsim} />)}
          {myEsims.length === 0 && !loadingMy && (
            <p className="text-zinc-500 text-sm py-10 text-center">No eSIMs yet — buy your first plan from the shop.</p>
          )}
        </div>
      )}

      {/* Purchase confirm dialog */}
      <Dialog open={!!confirmPkg} onOpenChange={(o) => !o && setConfirmPkg(null)}>
        <DialogContent data-testid="esim-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Confirm purchase</DialogTitle>
            <DialogDescription>{confirmPkg?.name}</DialogDescription>
          </DialogHeader>
          {confirmPkg && (
            <div className="space-y-4">
              <div className="flex justify-between text-sm"><span className="text-zinc-500">Price</span><span className="font-mono font-bold">${confirmPkg.price.toFixed(2)}</span></div>
              <div className="flex justify-between text-sm"><span className="text-zinc-500">Your balance</span><span className="font-mono">${(user?.balance ?? 0).toFixed(2)}</span></div>
              {(user?.balance ?? 0) < confirmPkg.price ? (
                <Link to="/wallet" className="flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-amber-100 text-amber-800 font-semibold text-sm" data-testid="esim-topup-link">
                  <Wallet className="h-4 w-4" /> Balance too low — top up first
                </Link>
              ) : (
                <button onClick={() => buy(confirmPkg)} disabled={buying !== null} data-testid="esim-confirm-buy"
                  className="w-full py-3 rounded-xl bg-primary text-white font-semibold hover:opacity-90 disabled:opacity-60 inline-flex items-center justify-center gap-2">
                  {buying ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                  Pay ${confirmPkg.price.toFixed(2)} from wallet
                </button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* QR / install dialog */}
      <Dialog open={!!qrEsim} onOpenChange={(o) => !o && setQrEsim(null)}>
        <DialogContent data-testid="esim-qr-dialog">
          <DialogHeader>
            <DialogTitle>Install your eSIM</DialogTitle>
            <DialogDescription>{qrEsim?.name}</DialogDescription>
          </DialogHeader>
          {qrEsim && (
            <div className="space-y-4">
              {qrEsim.qr_url ? (
                <img src={qrEsim.qr_url} alt="eSIM QR code" className="mx-auto h-52 w-52 rounded-xl border border-black/10" data-testid="esim-qr-image" />
              ) : (
                <p className="text-sm text-amber-700 text-center py-6">QR code is still being issued — check back shortly.</p>
              )}
              {qrEsim.ac && (
                <div className="bg-zinc-50 rounded-xl p-3 text-xs font-mono break-all border border-black/10">
                  <span className="text-zinc-400 font-sans font-semibold block mb-1">Manual activation code:</span>
                  {qrEsim.ac}
                </div>
              )}
              <ol className="text-sm text-zinc-600 space-y-1.5 list-decimal pl-5">
                <li>On the phone that will use the data, go to <b>Settings → Cellular/Mobile → Add eSIM</b></li>
                <li>Scan this QR code (or enter the manual code)</li>
                <li>Turn on the new line + data roaming when you arrive</li>
              </ol>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
